// ----------------------------------------------------------------------- imports externos
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import "../assets/css/herramientas.css";
import SubirPapel from "../components/taller/SubirPapel";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";
import {
  asignarPersona,
  corregirEntrega,
  cuando,
  DESTINATARIOS,
  destinatarioDe,
  diaAr,
  ELEMENTOS_COMUNES,
  entrega as traerEntrega,
  esDelCatalogo,
  ESTADOS_PAPEL,
  estadoPapel,
  figuraActivo,
  linkPapel,
  nombreDe,
  nombreDelPapel,
  nombrePanol,
  personas as traerPersonas,
  puedeVerEntregas,
  quitarPapel,
} from "../services/herramientas";
import { sugerencias as traerSugerencias } from "../services/actasSugerencias";
import { actaPara, EMPRESA, introPartida } from "../shared/textosConstancia";

/* El papel de una entrega del pañol: se imprime, se firma y se almacena.
 *
 * Nico, P-311 (05/10): *«Estaría bueno que al asignar nuevas herramientas ya
 * haga el papel solo, se imprima, se firma y se almacena»*.
 *
 * ── LA HOJA ES A4 Y SALE SOLA ────────────────────────────────────────────────
 *
 * Al imprimir se esconde todo el Hub —menú, encabezado, botones— y queda
 * sólo la hoja (`@media print` en herramientas.css). Es una pantalla del Hub
 * y no un iframe de FlotaBot: el acta vieja se tenía que BAJAR para poder
 * imprimirla («adentro del Hub el navegador no deja imprimir»), y acá
 * «Imprimir» imprime.
 *
 * ── LO QUE DICE EL PAPEL ES LO QUE SE GUARDÓ ─────────────────────────────────
 *
 * La lista sale de `panol_entregas.items`, congelada al entregar. El DNI y el
 * legajo se completan del padrón si la entrega no los trae (las actas viejas
 * no tenían legajo).
 *
 * ── CON EL PAPEL FIRMADO, SE VE EL FIRMADO (ticket #399) ─────────────────────
 *
 * Nico, 15/10: *«en lugar de ver la hoja no firmada que aparezca esa (la
 * firmada) […] para poder mostrar al chofer que la firmó él»*. Sobre todo las
 * 64 actas viejas, que se firmaron en papel antes del Hub: lo que se generaba
 * era una hoja limpia, sin firma, que no le prueba nada a nadie. Con la foto o
 * el escaneo subido, el firmado va arriba, «Imprimir» imprime ése, y la hoja
 * generada queda plegada.
 */

/* Casi todas las actas viejas tienen el interno: arriba se ofrecen los
   choferes que lo manejaron, primero los que además se llaman parecido a lo
   que leyó el reconocimiento (`actasSugerencias.js`). Con un toque queda;
   si no es ninguno, se busca a mano como siempre. */
function AsignarPersona({ entrega: acta, onListo, cambiando = false }) {
  const entregaId = acta.id;
  const [busca, setBusca] = useState("");
  const [gente, setGente] = useState([]);
  const [error, setError] = useState("");
  const [sugeridos, setSugeridos] = useState([]);

  useEffect(() => {
    let vivo = true;
    traerSugerencias([acta]).then((m) => {
      if (vivo) setSugeridos(m.get(String(acta.id)) || []);
    });
    return () => { vivo = false; };
  }, [acta]);

  useEffect(() => {
    if (busca.trim().length < 2) { setGente([]); return undefined; }
    let vivo = true;
    const t = setTimeout(async () => {
      // En el padrón ENTERO: el papel puede ser de alguien que ya no está
      // activo, o que tiene la ficha a medio cargar.
      const r = await traerPersonas(busca, { todos: true });
      if (vivo) setGente(r.filas || []);
    }, 200);
    return () => { vivo = false; clearTimeout(t); };
  }, [busca]);

  const elegir = async (p) => {
    const r = await asignarPersona(entregaId, p);
    if (!r.ok) { setError(r.error); return; }
    onListo();
  };

  return (
    <section className="hrr-caja-nueva cst-noimprimir" aria-label="De quién es">
      <b>{cambiando ? "¿De quién es en realidad?"
                    : "Este papel no tiene a nadie del padrón. ¿De quién es?"}</b>
      {sugeridos.length > 0 && (
        <div className="cst-sugeridos">
          <p className="tenue">
            El papel dice «{acta.persona_nombre}». Manejaron el interno {acta.interno}:
          </p>
          <ul className="hrr-caja-gente">
            {sugeridos.slice(0, 4).map((s) => (
              <li key={s.persona.dni}>
                <button type="button" onClick={() => elegir(s.persona)}>
                  <b>{s.puntos > 0 ? "¿Es " : ""}{nombreDe(s.persona)}{s.puntos > 0 ? "?" : ""}</b>
                  <small>
                    DNI {s.persona.dni}
                    {s.puntos > 0 ? " · el nombre se parece" : ""}
                    {" · tocá para asignarle el papel"}
                  </small>
                </button>
              </li>
            ))}
          </ul>
          <p className="tenue">¿No es ninguno? Buscalo:</p>
        </div>
      )}
      <input type="search" placeholder="Apellido, nombre o DNI" value={busca}
             onChange={(e) => setBusca(e.target.value)} aria-label="Buscar persona" />
      <ul className="hrr-caja-gente">
        {gente.slice(0, 8).map((p) => (
          <li key={p.dni}>
            <button type="button" onClick={() => elegir(p)}>
              <b>{nombreDe(p)}</b>
              <small>
                DNI {p.dni}{p.puesto ? ` · ${p.puesto}` : ""}
                {figuraActivo(p) ? "" : " · no figura activo"}
              </small>
            </button>
          </li>
        ))}
      </ul>
      {error && <p className="hrr-aviso">{error}</p>}
    </section>
  );
}

/* CORREGIR EL PAPEL A MANO (Nico, 07/10): «hay varios listados que no leyó
 * bien. Quiero corregir manualmente esos». Nombre y DNI (08/10: «me deja
 * corregir todo menos el nombre y apellido del chofer, ni agregarle el DNI»),
 * fecha, interno y la lista; cambiar
 * de quién es; y quitarlo si no es de nadie —un acta sin persona no tiene caja
 * desde donde quitarla—. Lo del catálogo no se toca acá: ver `esDelCatalogo`. */
function CorregirPapel({ entrega: e, onListo, onQuitado }) {
  const [nombre, setNombre] = useState(() => nombreDelPapel(e.persona_nombre));
  const [dni, setDni] = useState(e.persona_dni || "");
  const [fecha, setFecha] = useState(e.entregado_en || "");
  const [interno, setInterno] = useState(e.interno || "");
  const [items, setItems] = useState(() => (Array.isArray(e.items) ? e.items : []));
  const [nuevo, setNuevo] = useState("");
  const [error, setError] = useState("");
  const [cambiaPersona, setCambiaPersona] = useState(false);
  const [quitando, setQuitando] = useState(false);

  const poner = (i, campo, valor) =>
    setItems((l) => l.map((it, j) => (j === i ? { ...it, [campo]: valor } : it)));
  const sumar = () => {
    if (!nuevo.trim()) return;
    setItems((l) => [...l, { nombre: nuevo.trim(), cantidad: 1 }]);
    setNuevo("");
  };

  const guardar = async () => {
    const r = await corregirEntrega(e.id, {
      persona_nombre: nombre, persona_dni: dni, interno, entregado_en: fecha, items,
    });
    if (!r.ok) { setError(r.error); return; }
    setError("");
    onListo();
  };
  const quitar = async () => {
    const r = await quitarPapel(e.id);
    if (!r.ok) { setError(r.error); return; }
    onQuitado();
  };

  return (
    <details className="hrr-caja-nueva cst-noimprimir cst-corregir">
      <summary>✏️ Corregir este papel</summary>
      <label className="hrr-campo">
        Nombre y apellido
        <input type="text" value={nombre} onChange={(ev) => setNombre(ev.target.value)}
               aria-label="Nombre y apellido" />
      </label>
      <label className="hrr-campo">
        DNI
        <input type="text" inputMode="numeric" value={dni}
               onChange={(ev) => setDni(ev.target.value)} aria-label="DNI" />
      </label>
      <label className="hrr-campo">
        Fecha de entrega
        <input type="date" value={fecha} onChange={(ev) => setFecha(ev.target.value)}
               aria-label="Fecha de entrega" />
      </label>
      <label className="hrr-campo">
        Interno
        <input type="text" value={interno} onChange={(ev) => setInterno(ev.target.value)}
               aria-label="Interno" />
      </label>
      <ul className="cst-corregir-items" aria-label="Elementos del papel">
        {items.map((it, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={i}>
            {esDelCatalogo(it) ? (
              <span>{Number(it.cantidad) || 1} × {it.nombre} <small>(del catálogo: se saca desde la caja)</small></span>
            ) : (
              <>
                <input type="number" min="1" value={it.cantidad ?? 1}
                       onChange={(ev) => poner(i, "cantidad", ev.target.value)}
                       aria-label={`Cantidad de ${it.nombre}`} />
                <input type="text" value={it.nombre || ""}
                       onChange={(ev) => poner(i, "nombre", ev.target.value)}
                       aria-label={`Elemento ${i + 1}`} />
                <input type="text" value={it.nota || ""} maxLength={200}
                       placeholder="Observación"
                       onChange={(ev) => poner(i, "nota", ev.target.value)}
                       aria-label={`Observación de ${it.nombre || `elemento ${i + 1}`}`} />
                <button type="button" className="hrr-btn hrr-btn--chico"
                        onClick={() => setItems((l) => l.filter((_, j) => j !== i))}
                        aria-label={`Quitar ${it.nombre}`}>
                  Quitar
                </button>
              </>
            )}
          </li>
        ))}
      </ul>
      <div className="cst-corregir-sumar">
        <input type="text" list="cst-elementos" placeholder="Agregar un elemento"
               value={nuevo} onChange={(ev) => setNuevo(ev.target.value)}
               onKeyDown={(ev) => { if (ev.key === "Enter") { ev.preventDefault(); sumar(); } }}
               aria-label="Agregar un elemento" />
        <datalist id="cst-elementos">
          {ELEMENTOS_COMUNES.map((n) => <option key={n} value={n} />)}
        </datalist>
        <button type="button" className="hrr-btn hrr-btn--chico" onClick={sumar}>Agregar</button>
      </div>
      {error && <p className="hrr-aviso">{error}</p>}
      <div className="cst-corregir-pie">
        <button type="button" className="hrr-btn hrr-btn--principal" onClick={guardar}>
          Guardar la corrección
        </button>
        {e.persona_dni && (
          <button type="button" className="hrr-btn hrr-btn--chico"
                  onClick={() => setCambiaPersona((v) => !v)}>
            Es de otra persona
          </button>
        )}
        {!quitando && (
          <button type="button" className="hrr-btn hrr-btn--chico" onClick={() => setQuitando(true)}>
            Quitar este papel
          </button>
        )}
        {quitando && (
          <span role="group" aria-label="Confirmar">
            ¿Quitarlo?{" "}
            <button type="button" className="hrr-btn hrr-btn--chico" onClick={quitar}>Sí, quitarlo</button>{" "}
            <button type="button" className="hrr-btn hrr-btn--chico" onClick={() => setQuitando(false)}>No</button>
          </span>
        )}
      </div>
      {cambiaPersona && <AsignarPersona entrega={e} onListo={onListo} cambiando />}
    </details>
  );
}

const ConstanciaEntrega = () => {
  const { permisos = null } = useOutletContext() || {};
  const permitido = puedeVerEntregas(permisos);
  const puedeEditar = permisos !== null && permisos.includes("cargar.taller");
  const { id } = useParams();
  const [params] = useSearchParams();
  const recien = params.get("imprimir") === "1";
  const navegar = useNavigate();

  const [e, setE] = useState(undefined); // undefined = cargando, null = no está
  const [error, setError] = useState("");
  const [persona, setPersona] = useState(null);
  const [foto, setFoto] = useState(null);
  /* Nico, 13/10: «cuando guarda queda inerte y no pasa nada, y no se genera
     la caja». Guardaba bien, pero la pantalla no decía nada; y sin DNI el
     papel no es de nadie, así que no hay caja que armar. Ahora lo dice. */
  const [guardado, setGuardado] = useState(false);

  const cargar = useCallback(async () => {
    const r = await traerEntrega(id);
    setE(r.fila);
    setError(r.error || "");
    if (r.fila?.persona_dni) {
      const p = await traerPersonas(r.fila.persona_dni, { todos: true });
      setPersona((p.filas || []).find((x) => String(x.dni) === String(r.fila.persona_dni)) || null);
    }
    if (r.fila?.foto_path) setFoto(await linkPapel(r.fila.foto_path));
    else setFoto(null);
  }, [id]);

  useEffect(() => { if (permitido) cargar(); }, [permitido, cargar]);
  const alGuardar = useCallback(async () => { await cargar(); setGuardado(true); }, [cargar]);

  if (!permitido) return <PuertaCerrada />;
  if (e === undefined && !error) return <p className="hrr-vacio">Buscando el papel…</p>;
  if (error) return <p className="hrr-aviso">No se pudo consultar.</p>;
  if (!e) return <p className="hrr-vacio">No encontré ese papel.</p>;

  const destinatario = e.destinatario || (persona ? destinatarioDe(persona) : "mecanico");
  const acta = actaPara(destinatario);
  // Lo que dice el papel manda: es lo que se corrige a mano. El padrón rellena.
  const nombre = e.persona_nombre || (persona ? nombreDe(persona) : "");
  const intro = introPartida(destinatario, nombre);
  const items = Array.isArray(e.items) ? e.items : [];
  const dni = e.persona_dni || "";
  const legajo = e.persona_legajo || persona?.legajo || "";
  const est = ESTADOS_PAPEL[estadoPapel(e)];
  const esPdf = /\.pdf$/i.test(e.foto_path || "");
  const firmado = Boolean(e.foto_path);

  const hoja = (
    <article className="cst-hoja" aria-label="Papel para firmar">
      <header className="cst-cab">
        <div className="cst-marca">
          {EMPRESA.marca}<small>{EMPRESA.bajada}</small>
        </div>
        <div className="cst-dir">
          {EMPRESA.direcciones.map((d) => <div key={d}>{d}</div>)}
        </div>
      </header>
      <h2 className="cst-titulo">{acta.titulo}</h2>
      <div className="cst-meta">
        <span>Fecha: <b>{e.entregado_en ? diaAr(e.entregado_en) : "____/____/______"}</b></span>
        <span>N.º <b>{e.id}</b></span>
        {e.panol && <span>Pañol: <b>{nombrePanol(e.panol)}</b></span>}
        {(destinatario === "chofer" || e.interno) && (
          <span>Interno: <b>{e.interno || "______"}</b></span>
        )}
      </div>
      <div className="cst-meta">
        <span>{DESTINATARIOS[destinatario].rotulo}: <b>{nombre}</b></span>
        <span>DNI: <b>{dni || "______________"}</b></span>
        {legajo && <span>Legajo: <b>{legajo}</b></span>}
      </div>
      <p className="cst-leg">
        {intro.antes}<b>{intro.persona}</b>{intro.despues}
      </p>
      <table className="cst-items">
        <thead><tr><th>Cant.</th><th>Detalle</th><th>Código</th></tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={3}>—</td></tr>}
          {items.map((it, i) => (
            <tr key={i}>
              <td>{Number(it.cantidad) || 1}</td>
              <td>
                {it.nombre}
                {it.nota && <small className="cst-item-nota"> — {it.nota}</small>}
              </td>
              <td>{it.codigo || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {e.observaciones && !String(e.origen || "").startsWith("acta-papel") && (
        <p className="cst-obs">Observaciones: {e.observaciones}</p>
      )}
      {acta.parrafos.map((p) => <p key={p} className="cst-leg">{p}</p>)}
      <div className="cst-firmas">
        <div className="cst-firma"><div className="cst-linea">Firma de quien recibe</div></div>
        <div className="cst-firma"><div className="cst-linea">Aclaración y DNI</div></div>
        <div className="cst-firma">
          <div className="cst-linea">Entregó{e.entregador ? `: ${e.entregador}` : ""}</div>
        </div>
      </div>
    </article>
  );

  return (
    <div className="hrr cst">
      <div className="cst-barra cst-noimprimir">
        <Link className="hrr-caja-volver"
              to={dni ? `/taller/herramientas/caja/${encodeURIComponent(dni)}` : "/taller/herramientas"}>
          ← {dni ? "La caja" : "Todas las cajas"}
        </Link>
        <span className={`cst-estado ${est.clase}`}>
          {est.icono} {est.rotulo}
          {e.foto_subida_en ? ` · subida el ${cuando(e.foto_subida_en)}` : ""}
        </span>
        {!(firmado && esPdf) && (
          <button type="button" className="hrr-btn hrr-btn--principal"
                  onClick={() => window.print()}>
            🖨️ Imprimir{firmado ? " el firmado" : ""}
          </button>
        )}
        {firmado && esPdf && foto && (
          <a className="hrr-btn hrr-btn--principal" href={foto} target="_blank" rel="noreferrer">
            Abrir el PDF firmado
          </a>
        )}
        {puedeEditar && !e.foto_path && <SubirPapel entrega={e} onSubido={cargar} />}
      </div>
      {recien && !e.foto_path && (
        <p className="cst-listo cst-noimprimir" role="status">
          ✅ Entrega anotada. Imprimí el papel, que lo firme {nombre}, y subí acá la foto
          del papel firmado.
        </p>
      )}
      {guardado && (
        <p className="cst-listo cst-noimprimir" role="status">
          ✅ Guardado.{" "}
          {dni
            ? <Link to={`/taller/herramientas/caja/${encodeURIComponent(dni)}`}>Ver la caja de {nombre}</Link>
            : "Todavía no tiene caja: falta decir de quién es. Elegilo acá abajo o escribí el DNI en «Corregir este papel»."}
        </p>
      )}
      {puedeEditar && !dni && <AsignarPersona entrega={e} onListo={alGuardar} />}
      {e.anulado_en && (
        <p className="hrr-aviso cst-noimprimir" role="status">Este papel fue quitado.</p>
      )}
      {puedeEditar && !e.anulado_en && (
        <CorregirPapel key={`${e.id}-${e.persona_dni || ""}`} entrega={e} onListo={alGuardar}
                       onQuitado={() => navegar(dni
                         ? `/taller/herramientas/caja/${encodeURIComponent(dni)}`
                         : "/taller/herramientas")} />
      )}

      {firmado && (
        <section className="cst-foto cst-firmado" aria-label="Papel firmado">
          {!foto && <p className="hrr-aviso cst-noimprimir">No se pudo abrir el papel firmado.</p>}
          {foto && esPdf && (
            <object className="cst-pdf cst-noimprimir" data={foto} type="application/pdf"
                    aria-label={`Papel firmado de la entrega ${e.id}`}>
              <a className="hrr-btn" href={foto} target="_blank" rel="noreferrer">Abrir el PDF firmado</a>
            </object>
          )}
          {foto && !esPdf && (
            <a href={foto} target="_blank" rel="noreferrer">
              <img src={foto} alt={`Papel firmado de la entrega ${e.id}`} />
            </a>
          )}
          {puedeEditar && (
            <p className="tenue cst-noimprimir">
              ¿Es la foto equivocada? <SubirPapel entrega={e} onSubido={cargar} chico />
            </p>
          )}
        </section>
      )}

      {firmado ? (
        <details className="cst-generada cst-noimprimir">
          <summary>Ver la hoja sin firma</summary>
          {hoja}
        </details>
      ) : hoja}
    </div>
  );
};

export default ConstanciaEntrega;
